import { authFetch } from '@/api';
import { requireAuth } from '@/auth/requireAuth'
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react';
import { toast } from 'react-hot-toast';

export const Route = createFileRoute('/(app)/_layout/import')({
  component: RouteComponent,
  beforeLoad: requireAuth
})

function RouteComponent() {

  const now = new Date();
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [year, setYear] = useState<number>(now.getFullYear())

  const [importingActivities, setImportingActivities] = useState(false);
  const [importingCalendar, setImportingCalendar] = useState(false);
  const [importingPeople, setImportingPeople] = useState(false);
  const [importingRoles, setImportingRoles] = useState(false);
  const [importingCompanies, setImportingCompanies] = useState(false);


  const [activitiesFile, setActivitiesFile] = useState<File | null>(null);
  const [calendarFile, setCalendarFile] = useState<File | null>(null);
  const [peopleFile, setPeopleFile] = useState<File | null>(null);
  const [rolesFile, setRolesFile] = useState<File | null>(null);
  const [companiesFile, setCompaniesFile] = useState<File | null>(null);

  const importCompanies = async () => {
    // handle file upload logic here
    const formData = new FormData();
    if (companiesFile) {
      formData.append('file', companiesFile);
    }

    const loading = toast.loading("Importing companies...")
    try {
      setImportingCompanies(true);
      const response = await authFetch(`${import.meta.env.VITE_API_URL}/import/companies`, {
        method: 'POST',
        body: formData
      }, true);
      if (!response.ok) {
        throw new Error('Failed to import companies');
      }
      toast.success("Imported companies successfully");
    }
    catch (error) {
      toast.error("Import failed. Please try again.")
    } finally {
      toast.dismiss(loading);
      setImportingCompanies(false);
    }
  }

  const importActivities = async () => {
    // handle file upload logic here
    const loading = toast.loading("Importing activities...")
    try {
      setImportingActivities(true);
      const formData = new FormData();
      if (activitiesFile) {
        formData.append('file', activitiesFile);
      }
      formData.append('month', month.toString());
      formData.append('year', year.toString());
      const response = await authFetch(`${import.meta.env.VITE_API_URL}/import/activities`, {
        method: 'POST',
        body: formData,
      }, true);
      if (!response.ok) {
        throw new Error('Failed to import activities');
      }
      toast.success("Imported activities successfully");
    }
    catch (error) {
      toast.error("Import failed. Please try again.")
    }
    finally {
      toast.dismiss(loading);
      setImportingActivities(false);
    }
  }

  const importRoles = async () => {
    // handle file upload logic here
    const formData = new FormData();
    if (rolesFile) {
      formData.append('file', rolesFile);
    }

    const loading = toast.loading("Importing roles...")
    try {
      setImportingRoles(true);
      const response = await authFetch(`${import.meta.env.VITE_API_URL}/import/roles`, {
        method: 'POST',
        body: formData,
      }, true);
      if (!response.ok) {
        throw new Error('Failed to import roles');
      }
      toast.success("Imported roles successfully");
    }
    catch (error) {
      toast.error("Import failed. Please try again.")
    } finally {
      toast.dismiss(loading);
      setImportingRoles(false);
    }
  }

  const importPeople = async () => {
    // handle file upload logic here
    const formData = new FormData();
    if (peopleFile) {
      formData.append('file', peopleFile);
    }

    const loading = toast.loading("Importing people...")
    try {
      setImportingPeople(true);
      const response = await authFetch(`${import.meta.env.VITE_API_URL}/import/people`, {
        method: 'POST',
        body: formData,
      }, true);
      if (!response.ok) {
        throw new Error('Failed to import people');
      }
      toast.success("Imported people successfully");
    }
    catch (error) {
      toast.error("Import failed. Please try again.")
    }
    finally {
      toast.dismiss(loading);
      setImportingPeople(false);
    }
  }

  const importCalendar = async () => {
    // handle file upload logic here
    const formData = new FormData();
    if (calendarFile) {
      formData.append('file', calendarFile);
    }
    formData.append('year', year.toString());
    const loading = toast.loading("Importing calendar...")
    try {
      setImportingCalendar(true);
      const response = await authFetch(`${import.meta.env.VITE_API_URL}/import/calendar`, {
        method: 'POST',
        body: formData,
      }, true);
      if (!response.ok) {
        throw new Error('Failed to import calendar');
      }
      toast.success("Imported calendar successfully");
    }
    catch (error) {
      toast.error("Import failed. Please try again.")
    }
    finally {
      toast.dismiss(loading);
      setImportingCalendar(false);
    }
  }

  const handleSetStatuses = async () => {
    const loading = toast.loading("Setting statuses...")
    try {
      const response = await authFetch(`${import.meta.env.VITE_API_URL}/import/fill`, {
        method: 'POST',
      }, true);
      if (!response.ok) {
        throw new Error("Failed to set statuses")
      }
    }
    catch (error) {
      toast.error("Operation failed. Please try again.")
    }
    finally {
      toast.dismiss(loading);
    }
  }

  return (
    <div className="text-center grid grid-cols-1 md:grid-cols-3 gap-2 m-2">
      <Card>
        <CardHeader>
          <CardTitle>Import companies and people</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label>Companies</Label>
            <div className="flex items-center gap-2">

              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // handle file upload logic here
                    setCompaniesFile(file);
                  }
                }}
                className="mx-auto my-4 block"
              />
              <Button disabled={importingCompanies} onClick={importCompanies}>Import</Button>
            </div>
          </div>
          <div>
            <Label>Roles</Label>
            <div className="flex items-center gap-2">

              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // handle file upload logic here
                    setRolesFile(file);
                  }
                }}
                className="mx-auto my-4 block"
              />
              <Button disabled={importingRoles} onClick={importRoles}>Import</Button>
            </div>
          </div>
          <div>
            <Label>People</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // handle file upload logic here
                    setPeopleFile(file);
                  }
                }}
                className="mx-auto my-4 block"
              />
              <Button disabled={importingPeople} onClick={importPeople}>Import</Button>
            </div>
          </div>

        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Import calendar and activities</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <div className="flex gap-2 mb-4">
              <div>

                <Label htmlFor="month-select">Month</Label>
                <Select name="month-select" value={month.toString()} onValueChange={(value) => setMonth(+value)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Jan</SelectItem>
                    <SelectItem value="2">Feb</SelectItem>
                    <SelectItem value="3">Mar</SelectItem>
                    <SelectItem value="4">Apr</SelectItem>
                    <SelectItem value="5">May</SelectItem>
                    <SelectItem value="6">Jun</SelectItem>
                    <SelectItem value="7">Jul</SelectItem>
                    <SelectItem value="8">Aug</SelectItem>
                    <SelectItem value="9">Sep</SelectItem>
                    <SelectItem value="10">Oct</SelectItem>
                    <SelectItem value="11">Nov</SelectItem>
                    <SelectItem value="12">Dec</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="year">Year</Label>
                <Input name="year" type="number" placeholder="Year" value={year} onChange={(e) => setYear(+e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Activities</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // handle file upload logic here
                      setActivitiesFile(file);
                    }
                  }}
                  className="mx-auto my-4 block"
                />
                <Button disabled={importingActivities} onClick={importActivities}>Import</Button>
              </div>
            </div>
          </div>

          <div>
            <Label>Calendar</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // handle file upload logic here
                    setCalendarFile(file);
                  }
                }}
                className="mx-auto my-4 block"
              />
              <Button disabled={importingCalendar} onClick={importCalendar}>Import</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Status</CardTitle>
        </CardHeader>
        <CardContent>
          <Label>Set statuses</Label>
          <Button onClick={handleSetStatuses}>Set statuses</Button>
        </CardContent>
      </Card>

    </div>
  )
}
